import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Plus, Send, X } from "lucide-react";
import { ApiError } from "@/lib/api";
import { getTestRecipients, sendTest, type TestSendPreview } from "@/lib/emailApi";
import { useToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const plainAddress = (t: string) => /^[^@\s<>",]+@[^@\s<>",]+\.[^@\s<>",]+$/.test(t);

/** "Send test": Brandon's test list (no questions asked) plus up to 3 other people (Frank…), which the
 *  server holds back until he confirms a preview. It sends the last saved draft, with a sample person's details. */
export function TestSend({ emailId, subjectB }: { emailId: number | null; subjectB?: string }) {
  const toast = useToast();
  const lists = useQuery({ queryKey: ["email-test-recipients"], queryFn: getTestRecipients, staleTime: 60_000 });
  const testList = lists.data?.test_list ?? [];
  const max = lists.data?.max_extras ?? 3;
  const [picked, setPicked] = useState<Set<string> | null>(null); // null = the default (the whole test list)
  const chosen = picked ?? new Set(testList);
  const [extras, setExtras] = useState<string[]>([]);
  const [typed, setTyped] = useState("");
  const [problem, setProblem] = useState("");
  const [preview, setPreview] = useState<TestSendPreview | null>(null);
  const [useB, setUseB] = useState(false); // test the A/B broadcast's second subject line
  const testB = useB && !!subjectB?.trim();

  const to = testList.filter((a) => chosen.has(a));
  const send = useMutation({
    mutationFn: (confirm: boolean) => sendTest(emailId!, { to, extra: extras, confirm, subject: testB ? subjectB : undefined }),
    onMutate: () => setProblem(""),
    onSuccess: (r) => {
      if (r.kind === "sample") return toast("Test sends start working when the email module is merged.", "error");
      if (r.kind === "confirm") return setPreview(r.preview);
      setPreview(null);
      setExtras([]); // so a second click doesn't email the extra people again
      const queued = r.results.filter((x) => x.queued).map((x) => x.to).filter(Boolean);
      const skipped = r.results.filter((x) => !x.queued);
      toast(queued.length ? `Test sent to ${queued.join(", ")}` : "Nothing was sent", queued.length ? "ok" : "error");
      if (skipped.length) setProblem(`Not sent: ${skipped.map((x) => `${x.to ?? "?"} (${x.skipped ?? "skipped"})`).join("; ")}`);
    },
    onError: (e) => {
      setPreview(null);
      // 403: an extra address is blocked, unsubscribed or archived; 400: not a plain address, or too many.
      setProblem(e instanceof ApiError && (e.status === 403 || e.status === 400) ? e.message : `Couldn't send the test: ${e.message}`);
    },
  });

  const addExtra = (raw: string) => {
    const a = raw.trim().replace(/^mailto:/i, "");
    if (!a) return;
    if (!plainAddress(a)) return setProblem(`“${a}” isn't a plain email address (no names or brackets).`);
    if (testList.some((x) => x.toLowerCase() === a.toLowerCase())) {
      setPicked(new Set([...chosen, testList.find((x) => x.toLowerCase() === a.toLowerCase())!]));
      setTyped("");
      return;
    }
    if (extras.some((x) => x.toLowerCase() === a.toLowerCase())) return setTyped("");
    if (extras.length >= max) return setProblem(`At most ${max} other people per test. A test isn't a mailing.`);
    setExtras([...extras, a]);
    setTyped("");
    setProblem("");
  };
  const savedLeft = (lists.data?.saved_extras ?? []).filter((a) => !extras.some((x) => x.toLowerCase() === a.toLowerCase()));
  const total = to.length + extras.length;

  if (lists.data === null) {
    // Older server without the test-recipient lists: nothing to show but the button's explanation.
    return <p className="text-xs text-[var(--color-muted-foreground)]">Test sends arrive with the email module.</p>;
  }
  return (
    <div className="space-y-2 rounded-md border p-3 text-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-medium">Send a test</span>
        <span className="text-xs text-[var(--color-muted-foreground)]">The last saved draft, “[TEST]” in the subject, with a sample person's details.</span>
      </div>
      {testList.length === 0 ? (
        <p className="text-xs text-[var(--color-muted-foreground)]">Brandon's test list is empty (setting email.test_recipients).</p>
      ) : (
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {testList.map((a) => (
            <label key={a} className="flex items-center gap-1.5">
              <Checkbox
                checked={chosen.has(a)}
                onChange={(e) => {
                  const next = new Set(chosen);
                  if (e.target.checked) next.add(a);
                  else next.delete(a);
                  setPicked(next);
                }}
              />
              {a}
            </label>
          ))}
        </div>
      )}
      <div className="space-y-1.5">
        <div className="text-xs text-[var(--color-muted-foreground)]">
          Also send to (up to {max}, asks you to confirm first):
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {extras.map((a) => (
            <Badge key={a} variant="warning">
              {a}
              <button aria-label={`Remove ${a}`} onClick={() => setExtras(extras.filter((x) => x !== a))}>
                <X className="size-3" />
              </button>
            </Badge>
          ))}
          {extras.length < max &&
            savedLeft.map((a) => (
              <button
                key={a}
                className="inline-flex items-center gap-1 rounded-full border border-dashed px-2 py-0.5 text-xs text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)] hover:text-[var(--color-foreground)]"
                onClick={() => addExtra(a)}
              >
                <Plus className="size-3" /> {a}
              </button>
            ))}
          {extras.length < max && (
            <form
              className="flex items-center gap-1"
              onSubmit={(e) => {
                e.preventDefault();
                addExtra(typed);
              }}
            >
              <Input className="h-7 w-56 text-xs" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="someone@example.com, then Enter" aria-label="Add another address to the test" />
            </form>
          )}
        </div>
      </div>
      {problem && (
        <p role="alert" className="text-xs text-[var(--color-danger-fg)]">
          {problem}
        </p>
      )}
      {!!subjectB?.trim() && (
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={useB} onChange={(e) => setUseB(e.target.checked)} />
          Use the second subject line (A/B test): {subjectB}
        </label>
      )}
      <Button size="sm" variant="outline" disabled={!emailId || total === 0 || send.isPending} title={emailId ? undefined : "Save the draft first"} onClick={() => send.mutate(false)}>
        <Send /> {total ? `Send test to ${total} ${total === 1 ? "address" : "addresses"}` : "Send test"}
      </Button>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="w-[min(520px,calc(100vw-2rem))]">
          <DialogTitle>Send this test to people outside the test list?</DialogTitle>
          <DialogDescription>{preview?.note ?? "Each extra address is recorded in the audit log."}</DialogDescription>
          {preview && (
            <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm">
              <dt className="text-[var(--color-muted-foreground)]">Email</dt>
              <dd>{preview.email ?? "—"}</dd>
              <dt className="text-[var(--color-muted-foreground)]">Subject</dt>
              <dd>[TEST] {preview.subject ?? ""}</dd>
              <dt className="text-[var(--color-muted-foreground)]">Test list</dt>
              <dd>{preview.test_list?.length ? preview.test_list.join(", ") : "nobody"}</dd>
              <dt className="font-medium">Also to</dt>
              <dd className="font-medium">{preview.extra_people?.join(", ")}</dd>
            </dl>
          )}
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setPreview(null)}>
              Cancel
            </Button>
            <Button disabled={send.isPending} onClick={() => send.mutate(true)}>
              Send the test
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
